import { useCallback, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { useTracking } from "utilities/analytics/analytics";

import type { ISearchHit } from "commonComponents/utilities/MainNavigation/AiSearch/types";

/**
 * `Learn` telemetry for the search dialog, sent through `useTracking` (Sawmill).
 *
 * Logs the join key (`search_id`, which the service records as
 * `btsearch.client_search_id`) plus what only the browser sees: clicks, ranks,
 * abandonment, unroutable drops, felt latency. Ids, counts, durations and enums
 * only — no titles, summaries or prose; `query` is the deliberate exception.
 */

/**
 * Downstream pipelines filter on `component`, so this keeps the dialog a
 * separate series from the dropdown's.
 */
const COMPONENT = "SearchDialog";

/** Caps the dropped-shape breakdown so one bad payload can't inflate an event. */
const MAX_DROPPED_SHAPES = 10;

export type SearchDialogSurface = "dialog" | "askPage";

/** How the dialog was opened. */
export type DialogTrigger = "icon" | "hotkey";

/**
 * Why the dialog closed, in the user's terms rather than the code's.
 *
 * `abandoned` is the one that matters: the service sees a search with no
 * successor and cannot tell "read the answer off the list and moved on" from
 * "gave up".
 */
export type DialogOutcome = "abandoned" | "record_opened" | "handed_off";

/**
 * Which list a rank indexes. The dialog only ever sends `committed` — it
 * renders nothing else — but the stream carries both, so the field stays
 * rather than the service having to assume.
 */
export type RankSource = "committed" | "preview";

/** What armed Ask — the service computes its own `is_question` and cannot know this. */
export type AskArmedBy = "auto" | "toggle" | "zero-results";

export type NavMethod = "click" | "keyboard";

export type RecordOpenedFrom = "result" | "recent" | "citation";

/**
 * How far one search got. Each is sent at most once per search.
 *
 * - `complete`: the stream finished.
 * - `interrupted`: the dialog closed, or the input was cleared, while the
 *   search was still running, with no row from it opened and nothing handed
 *   to Ask.
 * - `abandoned`: the search completed, then the dialog closed or the input was
 *   cleared with no row from it opened and nothing handed to Ask.
 *
 * A search superseded by the next one sends nothing further.
 */
export type SearchPhase = "complete" | "interrupted" | "abandoned";

/** A record the client could not route, named by shape rather than identity. */
export interface IDroppedShape {
    entity: string;
    /** The payload keys the hit DID carry, so the missing one is inferable. */
    payloadKeys: string[];
}

export interface IRoutability {
    renderedCount: number;
    droppedUnroutable: number;
    droppedShapes: IDroppedShape[];
}

/**
 * Routable/dropped split of a committed ranking, one aggregate per search.
 * `payloadKeys` names what a dropped hit did carry, without logging the record.
 */
export function summarizeRoutability(
    committed: readonly ISearchHit[] | null,
    isRoutable: (hit: ISearchHit) => boolean,
): IRoutability {
    const hits = committed ?? [];
    const dropped = hits.filter((hit) => !isRoutable(hit));
    const shapes = new Map<string, IDroppedShape>();
    for (const hit of dropped) {
        const keys = Object.keys(hit.payload ?? {}).sort();
        const key = `${hit.entity}:${keys.join(",")}`;
        if (!shapes.has(key)) {
            shapes.set(key, { entity: hit.entity, payloadKeys: keys });
        }
    }
    return {
        renderedCount: hits.length - dropped.length,
        droppedUnroutable: dropped.length,
        droppedShapes: [...shapes.values()],
    };
}

export interface ISearchDialogLearnContext {
    surface: SearchDialogSurface;
    /** `v1` or `v2` — a flag-state sanity check on every event. */
    apiVersion: string;
}

interface IEnvelopeIds {
    searchId: string | null;
    traceId: string | null;
    sessionId?: string | null;
    turnId?: string | null;
}

export interface ISearchDialogLearn {
    /** The id joining every event from one opening of the dialog. */
    openId: string;
    learnDialogOpened: (payload: {
        trigger: DialogTrigger;
        openRecordEntity: string | null;
        recentCount: number;
        promptCount: number;
    }) => void;
    learnDialogClosed: (payload: {
        outcome: DialogOutcome;
        msOpen: number;
        searchCount: number;
        askCount: number;
        sawResults: boolean;
    }) => void;
    learnSearchPhase: (
        ids: IEnvelopeIds,
        payload: {
            searchPhase: SearchPhase;
            query: string;
            resultCount: number;
            renderedCount: number;
            droppedUnroutable: number;
            droppedShapes: IDroppedShape[];
            rankSource: RankSource;
            durationMs: number | undefined;
            /** Until the service's ranking landed and its rows rendered, if it did. */
            timeToRowsMs: number | undefined;
        },
    ) => void;
    learnRecordOpened: (
        ids: IEnvelopeIds,
        payload: {
            from: RecordOpenedFrom;
            entity: string;
            recordId: string;
            rank: number | null;
            rankSource: RankSource | null;
            navMethod: NavMethod;
            timeToClickMs: number | undefined;
            msFromOpen: number;
        },
    ) => void;
    learnAskArmed: (ids: IEnvelopeIds, payload: { armedBy: AskArmedBy }) => void;
    learnPromptPicked: (payload: { promptIndex: number; promptCount: number }) => void;
    learnCorrectionApplied: (ids: IEnvelopeIds) => void;
    /**
     * The inline guess taken (Tab, right arrow or End). Lengths and where the
     * guess came from, never the text.
     */
    learnGuessAccepted: (
        ids: IEnvelopeIds,
        payload: {
            key: GuessKey;
            guessFrom: GuessSource;
            typedLength: number;
            guessLength: number;
        },
    ) => void;
    /** A typeahead name picked to search for: its 1-based place and kind, never the name. */
    learnSuggestionPicked: (
        ids: IEnvelopeIds,
        payload: { rank: number; count: number; entity: string; navMethod: NavMethod },
    ) => void;
    /** A suggested question picked (Needs attention): its place and tone, never the text. */
    learnSuggestedQuestionPicked: (payload: {
        rank: number;
        count: number;
        source: string;
        tone: string;
        hadCount: boolean;
    }) => void;
}

/** Which key took the inline guess. */
export type GuessKey = "tab" | "arrow" | "end";
/** Where the inline guess came from: the server's whole-name completion, a predicted query, or a listed name. */
export type GuessSource = "completion" | "prediction" | "name";

/**
 * Builds the dialog's telemetry callbacks, all sharing one `openId`.
 *
 * A hook rather than bare functions because `useTracking` is one, and because
 * the `openId` has to be minted once per mount — the dialog is mounted on open
 * and unmounted on close, so its lifetime is exactly the episode the id names.
 */
export function useSearchDialogLearn(context: ISearchDialogLearnContext): ISearchDialogLearn {
    const { trackEvent } = useTracking({ component: COMPONENT });
    // Lazy state rather than a ref: a ref would have to be read during render
    // to seed it, and the value only has to survive re-renders, not be mutable.
    const [openId] = useState(() => uuidv4());
    const { surface, apiVersion } = context;

    const envelope = useCallback(
        (ids?: IEnvelopeIds) => ({
            openId,
            surface,
            apiVersion,
            searchId: ids?.searchId ?? null,
            traceId: ids?.traceId ?? null,
            sessionId: ids?.sessionId ?? null,
            turnId: ids?.turnId ?? null,
        }),
        [openId, surface, apiVersion],
    );

    return useMemo<ISearchDialogLearn>(
        () => ({
            openId,

            learnDialogOpened: (payload) =>
                trackEvent({
                    event: "ModalOpen",
                    extraInfo: { ...envelope(), action: "dialog_opened", ...payload },
                }),

            learnDialogClosed: (payload) =>
                trackEvent({
                    event: "ModalClose",
                    extraInfo: { ...envelope(), action: "dialog_closed", ...payload },
                }),

            learnSearchPhase: (ids, payload) =>
                trackEvent({
                    event: "Search",
                    extraInfo: {
                        ...envelope(ids),
                        ...payload,
                        droppedShapes: payload.droppedShapes.slice(0, MAX_DROPPED_SHAPES),
                    },
                }),

            learnRecordOpened: (ids, payload) =>
                trackEvent({
                    event: "LinkClick",
                    element: "Link",
                    uniqueId: payload.entity,
                    extraInfo: { ...envelope(ids), action: "record_opened", ...payload },
                }),

            learnAskArmed: (ids, payload) =>
                trackEvent({
                    event: "OptionSelected",
                    extraInfo: { ...envelope(ids), action: "ask_armed", ...payload },
                }),

            learnPromptPicked: (payload) =>
                trackEvent({
                    event: "OptionSelected",
                    // The index, never the prompt text: the list is fixed and
                    // curated, so the index identifies it exactly.
                    extraInfo: { ...envelope(), action: "prompt_picked", ...payload },
                }),

            learnCorrectionApplied: (ids) =>
                trackEvent({
                    event: "OptionSelected",
                    extraInfo: { ...envelope(ids), action: "correction_applied" },
                }),

            learnGuessAccepted: (ids, payload) =>
                trackEvent({
                    event: "OptionSelected",
                    extraInfo: { ...envelope(ids), action: "guess_accepted", ...payload },
                }),

            learnSuggestionPicked: (ids, payload) =>
                trackEvent({
                    event: "OptionSelected",
                    extraInfo: { ...envelope(ids), action: "suggestion_picked", ...payload },
                }),

            learnSuggestedQuestionPicked: (payload) =>
                trackEvent({
                    event: "OptionSelected",
                    extraInfo: { ...envelope(), action: "suggested_question_picked", ...payload },
                }),
        }),
        [openId, envelope, trackEvent],
    );
}
